// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2024-05-16 14:21:37 +0200 (Thu, 16 May 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 142851 $
//  Purpose   : reducers for project mordor
// =============================================================================
// ---Third-party imports

// ---Framework imports
import {dict, mapItems, values} from 'ldash'
import log from 'uix/logging'
import {handleSubState, handleAction} from 'uix/actions'

// ---Local imports
import {
  sendFrame,
  setImageControl,
  clearInbookSearch,
  setInbookSearch,
  setI3F,
  setViewerGeometry,
  setFocusedViewerGeometry,
  setSelectedViewerGeometry,
  setModifiedViewerGeometry,
  clearFocusedViewerGeometry,
} from './actions'

// ---Exports
const SIZE_QUEUE = 4

let i3f = handleSubState('i3f', [
  handleAction(
    setI3F,
    ({manifestById, ...i3f}, {payload}) => ({
      ...i3f,
      manifestById: {...manifestById, [payload.manifest]: payload.data},
    }),
    {manifestById: {}}
  ),
])

let viewer = handleSubState(
  'viewer',
  [
    handleAction(sendFrame, ({viewerFrame, ...data}, {payload}) => ({
      ...data,
      viewerFrame: {...viewerFrame, [payload.viewerId]: payload},
    })),
    handleAction('GO', data => ({
      ...data,
      viewerFrame: {}, // reset - XXX: why does this not work with activateView
    })),
    handleAction(clearInbookSearch, (data, {payload}) => ({
      ...data,
      activeInBookSearches: {...data.activeInBookSearches, [payload.viewerId]: null},
    })),
    handleAction(setInbookSearch, (data, {payload}) => ({
      ...data,
      activeInBookSearches: {...data.activeInBookSearches, [payload.viewerId]: payload.searchData},
    })),
    handleAction(setImageControl, (data, {payload}) => {
      // XXX: this was the first failed(?) approach
      let {state: imgState, src: imgSrc} = payload

      let numLoading = values(data.imageControl).filter(e => e === 'loading').length

      let initiated = mapItems(
        data.imageControl,
        (src, state) => src,
        (src, state) => src !== imgSrc && state === 'initiated'
      )
      //~ log(initiated)
      //~ if (imgState === 'initiated' && initiated.length < NUM_QUEUE) {
      //~ imgState = 'queued'
      //~ }
      let numQueued = Math.max(SIZE_QUEUE - numLoading, 0)
      let toBeQueued = dict(initiated.slice(0, numQueued).map(src => [src, 'queued']))

      let imageControl = {...data.imageControl, ...toBeQueued, [imgSrc]: imgState}

      return {
        ...data,
        imageControl,
      }
    }),
    handleAction(setViewerGeometry, ({geometry, ...viewer}, {payload}) => {
      let {viewerId: vid, geometry: new_geometry} = payload
      if (!new_geometry) {
        // flush all otids for vid
        geometry = {...geometry, [vid]: {}}
      } else {
        let prev_vid_geo = geometry[vid] || {}
        // keep prev geoms for supporting multiple geometry sources
        let geoms = dict(
          mapItems(new_geometry, (otid, geoms) => [otid, {...(prev_vid_geo[otid] ?? {}), ...geoms}])
        )
        geometry = {...geometry, [vid]: geoms}
      }
      return {...viewer, geometry}
    }),
    handleAction(setFocusedViewerGeometry, ({focusedGeometry, ...viewer}, {payload}) => {
      let {viewerId, otid, geomtype, index} = payload
      //~ let k = `${viewerId}_${otid}_${geomtype}`
      return {...viewer, focusedGeometry: {...focusedGeometry, [viewerId]: {otid, geomtype, index}}}
    }),
    handleAction(setSelectedViewerGeometry, ({selectedGeometry, ...viewer}, {payload}) => {
      let {viewerId, otid, geomtype, items, clearOther} = payload
      let prev_otids = clearOther ? {} : selectedGeometry[viewerId] || {}
      let prev_geom = prev_otids[otid] || {}

      return {
        ...viewer,
        selectedGeometry: {
          ...selectedGeometry,
          [viewerId]: {...prev_otids, [otid]: {...prev_geom, [geomtype]: items}},
        },
      }
    }),
    handleAction(setModifiedViewerGeometry, ({modifiedGeometry, ...viewer}, {payload}) => {
      let {viewerId, otid, geomtype, items} = payload
      let k = `${viewerId}_${otid}`
      let prev_geom = modifiedGeometry[k] || {}
      let prev_items = prev_geom[geomtype] || {}
      // reset if items === null else merge
      items = items ? {...prev_items, ...items} : {}
      return {
        ...viewer,
        modifiedGeometry: {
          ...modifiedGeometry,
          [k]: {...prev_geom, [geomtype]: items},
        },
      }
    }),
    handleAction(clearFocusedViewerGeometry, viewer => ({...viewer, focusedGeometry: {}})),
  ],
  {
    geometryById: {},
    imageLocsById: {},
    viewerFrame: {},
    activeInBookSearches: {},
    inBookSearchById: {},
    imageControl: {},
    geometry: {}, // key: vid, value {page_otid: geometry-data}
    focusedGeometry: {}, // key: vid: value: {otid, geomtype, index}
    selectedGeometry: {}, // key: vid value: {page_otid: {geomtype: index}}
    modifiedGeometry: {}, // key: vid_page_otid_geomtype, value: geometry items (by item id)
  }
)

export const data = [i3f, viewer]
